// Menú "..." de cada sección del tablero de Mis tareas.
// Sigue el patrón de los demás popovers del módulo: portal al body,
// posicionado por rect y cierre al hacer clic fuera.
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    Pencil, Palette, Filter, ArrowLeft, ArrowRight, Copy, EyeOff, Eye, Trash2, Check
} from 'lucide-react';
import { RULES, COLOR_KEYS, getColors, getRuleLabel } from '../services/myTasksBoard';

const BoardColumnMenu = ({ column, position, isFirst, isLast, hideEmpty, onAction, onClose }) => {
    const ref = useRef(null);
    const [panel, setPanel] = useState(null); // null | 'color' | 'rule'

    useEffect(() => {
        const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('mousedown', onClickOutside);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onClickOutside);
            document.removeEventListener('keydown', onKey);
        };
    }, [onClose]);

    if (!position) return null;

    const style = { top: position.top + 6, left: position.left, zIndex: 80 };
    if (window.innerWidth - position.left < 270) { style.left = 'auto'; style.right = 16; }

    const Item = ({ icon: Icon, label, onClick, danger, disabled, trailing }) => (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`w-full text-left px-3 py-2 flex items-center gap-3 transition-colors text-[13px]
        ${disabled ? 'text-gray-300 cursor-not-allowed' : danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-100'}`}
        >
            <Icon size={15} className={disabled ? 'text-gray-300' : danger ? 'text-red-500' : 'text-gray-500'} />
            <span className="flex-1">{label}</span>
            {trailing}
        </button>
    );

    const act = (action, value) => { onAction(action, column, value); onClose(); };

    return createPortal(
        <div
            ref={ref}
            style={style}
            className="fixed w-64 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden animate-in fade-in zoom-in-95 duration-100"
        >
            {panel === 'color' ? (
                <div className="p-3">
                    <div className="flex items-center gap-2 mb-3">
                        <button onClick={() => setPanel(null)} className="text-gray-400 hover:text-gray-700"><ArrowLeft size={14} /></button>
                        <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Color de la sección</span>
                    </div>
                    <div className="grid grid-cols-5 gap-2">
                        {COLOR_KEYS.map(key => (
                            <button
                                key={key}
                                onClick={() => act('color', key)}
                                className={`h-8 rounded-lg flex items-center justify-center transition-all hover:scale-105 ${getColors(key).soft} ${column.color === key ? `ring-2 ${getColors(key).ring}` : ''}`}
                                title={key}
                            >
                                <span className={`w-3.5 h-3.5 rounded-full ${getColors(key).dot}`} />
                            </button>
                        ))}
                    </div>
                </div>
            ) : panel === 'rule' ? (
                <div className="py-2 max-h-80 overflow-y-auto">
                    <div className="flex items-center gap-2 px-3 pb-2 mb-1 border-b border-gray-100">
                        <button onClick={() => setPanel(null)} className="text-gray-400 hover:text-gray-700"><ArrowLeft size={14} /></button>
                        <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Qué entra aquí</span>
                    </div>
                    {RULES.map(rule => (
                        <button
                            key={rule.id}
                            onClick={() => act('rule', rule.id)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 transition-colors flex items-start gap-2.5"
                        >
                            <span className="w-4 pt-0.5">
                                {column.rule === rule.id && <Check size={14} className="text-blue-600" />}
                            </span>
                            <span className="flex-1">
                                <span className="block text-[13px] text-gray-800 font-medium">{rule.label}</span>
                                <span className="block text-[10.5px] text-gray-400 leading-tight">{rule.hint}</span>
                            </span>
                        </button>
                    ))}
                </div>
            ) : (
                <div className="py-1">
                    <div className="px-3 pt-2 pb-2 border-b border-gray-100">
                        <div className="text-[13px] font-bold text-gray-800 truncate">{column.title}</div>
                        <div className="text-[10.5px] text-gray-400">Regla: {getRuleLabel(column.rule)}</div>
                    </div>

                    <div className="py-1">
                        <Item icon={Pencil} label="Renombrar la sección" onClick={() => act('rename')} />
                        <Item icon={Filter} label="Cambiar qué entra aquí" onClick={() => setPanel('rule')} />
                        <Item icon={Palette} label="Color de la sección" onClick={() => setPanel('color')} />
                    </div>

                    <div className="py-1 border-t border-gray-100">
                        <Item icon={ArrowLeft} label="Mover a la izquierda" onClick={() => act('move_left')} disabled={isFirst} />
                        <Item icon={ArrowRight} label="Mover a la derecha" onClick={() => act('move_right')} disabled={isLast} />
                        <Item icon={Copy} label="Duplicar sección" onClick={() => act('duplicate')} />
                    </div>

                    <div className="py-1 border-t border-gray-100">
                        <Item
                            icon={hideEmpty ? Eye : EyeOff}
                            label={hideEmpty ? 'Mostrar secciones vacías' : 'Ocultar secciones vacías'}
                            onClick={() => act('toggle_hide_empty')}
                        />
                        <Item icon={Trash2} label="Eliminar la sección" onClick={() => act('delete')} danger />
                    </div>
                </div>
            )}
        </div>,
        document.body
    );
};

export default BoardColumnMenu;
